import type { LiveBotLevel } from './seats.ts';

/** How wide and deep one search looks. */
export type SearchKnobs = {
  /** Lines kept per turn of lookahead. */
  width: number;
  /** Turns simulated before committing the first. */
  depth: number;
  /** Reach samples per expanded line. */
  density: number;
  /** Reach samples for the click actually being chosen. */
  rootDensity: number;
};

/**
 * How hard a level searches, and how it treats other boats. Only Expert is built so far: every
 * level sails the same planner, and only Expert reads its rivals (Sequential).
 */
export type BotLevelKnobs = SearchKnobs & {
  /** Ranked clicks checked against the exact rules before giving up on a clean move. */
  verify: number;
  /**
   * Read rivals: forecast each one's line, so forecast hulls, a following distance, and blocking
   * can act on it. Off: rivals are parked rocks.
   */
  anticipate?: boolean;
  /**
   * Squeeze: a nudge is allowed, and clicks that brush a parked rival are tried through the real
   * sweep. Off: any boat contact is refused. Unset follows `anticipate`.
   */
  squeeze?: boolean;
  /** How many of the rival's turns one of this bot's turns is worth giving up to block. 0 never blocks. */
  aggression?: number;
  /** The light search used to predict a rival, and to judge a block. */
  rival?: SearchKnobs;
  /** Clicks that touch a parked rival, tried against the real sweep (a nudge is free). */
  contactRoots?: number;
  /** Clean clicks weighed for how much they hold a rival up. */
  blockRoots?: number;
  /** A block may cost this bot at most this many turns. */
  blockMaxCostTurns?: number;
  /** A block has to hold a rival up by at least this many turns to be believed. */
  blockMinGainTurns?: number;
  /**
   * Turns charged for sailing through where a rival is predicted to be parked, by move (index 0
   * is this bot's second move). A prediction is a guess, so it costs rather than forbids.
   */
  predictedContactTurns?: readonly number[];
  /** Treat where each rival is forecast to park as a hull to keep clear of. Default on. */
  forecastHulls?: boolean;
  /** Keep clear of where each rival would be if it braked as hard as it can. Default on. */
  followDistance?: boolean;
  /**
   * What the bot knows about the course past its lookahead. `time` (default) is the corner-aware
   * time field plus the turning limit; `distance` only knows how far it is, so it does not see a
   * bend coming until the bend is inside its lookahead.
   */
  courseSense?: 'time' | 'distance';
  /**
   * Turns lost a bot shrugs off when picking among its clean clicks: a softmax over each click's
   * estimated turns, so a slip is usually small and a big one rare. 0 always takes the best.
   */
  temperature?: number;
  /** Metres of aim wobble on the click, along and (half as much) across, growing with speed. */
  aimErrorM?: number;
  /**
   * How hard the bot thinks it can brake, as a share of the truth. Above 1 it carries too much
   * speed toward land and has to scrub it late; below 1 it backs off early. 1 is exact.
   */
  brakeConfidence?: number;
  /**
   * The fastest it lets itself sail, in display km/h; 0 or unset has no limit. A careful bot's
   * steady loss: it never needs aim error to be slow, and slower is never closer to a crash.
   */
  speedLimitKmh?: number;
};

export const RIVAL_KNOBS: SearchKnobs = { width: 12, depth: 4, density: 24, rootDensity: 32 };

/**
 * Expert races by squeezing: nudges are free and tried through the real sweep, a tap from behind
 * or any paid hit is refused, and a forced one is taken as slowly as possible. Forecast hulls, a
 * following distance, and blocking are off: in mixed Sequential races (8 maps, both seat orders)
 * each one cost the bot that used it between a fifth and two thirds of a place. They stay as
 * knobs for bot personalities.
 */
export const EXPERT_KNOBS: BotLevelKnobs = {
  width: 48,
  depth: 8,
  density: 40,
  rootDensity: 96,
  verify: 12,
  anticipate: true,
  aggression: 0,
  rival: RIVAL_KNOBS,
  contactRoots: 8,
  blockRoots: 6,
  forecastHulls: false,
  followDistance: false,
};

/**
 * Expert's knobs with a skill handicap. `s` 0 is Expert; 1 is the clumsiest bot we model:
 * - plans 3 turns ahead instead of 8;
 * - past halfway, only knows distances, not bends;
 * - slips by up to 1.5 turns;
 * - aims up to 6 m off;
 * - thinks its brakes are 60% better than they are.
 * Every level races by the same rules (nudges allowed, never a paid hit by choice); only skill
 * differs.
 */
export function skillKnobs(s: number): BotLevelKnobs {
  const k = Math.min(1, Math.max(0, s));

  if (k === 0) {
    return EXPERT_KNOBS;
  }

  return {
    ...EXPERT_KNOBS,
    depth: Math.round(EXPERT_KNOBS.depth - 5 * k),
    courseSense: k >= 0.5 ? 'distance' : 'time',
    temperature: 1.5 * k * k,
    aimErrorM: 6 * k,
    brakeConfidence: 1 + 0.6 * k,
  };
}

/**
 * Skill handicap per level, from `scripts/bot-playtest/tune.run.ts` (BOT_TUNE=1): the `s` whose
 * mean boat-clock over 6 maps and 2 seeds lands on the level's share of Expert's. Measured
 * 2026-10-07: Easy 139%, Medium 114%, Hard 105%. Hard was first tuned to 102% (s 0.05), but in a
 * four-level pack it beat Expert on 5 of 6 maps: 2% is smaller than the seat-order effect.
 */
export const LEVEL_SKILL: Readonly<Record<LiveBotLevel, number>> = {
  easy: 0.69,
  medium: 0.31,
  hard: 0.125,
  expert: 0,
};

/** Target share of Expert's boat-clock each level is tuned to. */
export const LEVEL_PACE_TARGET: Readonly<Record<LiveBotLevel, number>> = {
  easy: 1.4,
  medium: 1.15,
  hard: 1.05,
  expert: 1,
};

export const BOT_LEVEL_KNOBS: Readonly<Record<LiveBotLevel, BotLevelKnobs>> = {
  easy: skillKnobs(LEVEL_SKILL.easy),
  medium: skillKnobs(LEVEL_SKILL.medium),
  hard: skillKnobs(LEVEL_SKILL.hard),
  expert: EXPERT_KNOBS,
};
